import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Camera, Users, Download, Star } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useEvents } from '@context/EventContext';
import { photoAPI } from '@services/api';
import { ROUTES } from '@utils/constants';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import ResponsiveImage from '@components/common/ResponsiveImage';
import type { Photo } from '@/types';

const HomePage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [featuredPhotos, setFeaturedPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  useEvents(); // Initialize events context

  useEffect(() => {
    const loadFeaturedPhotos = async () => {
      try {
        const response = await photoAPI.getFeaturedPhotos();
        setFeaturedPhotos(response.data.slice(0, 6));
      } catch {
        // Featured photos are non-critical; page still renders without them
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedPhotos();
  }, []);

  // Logged-in users have no reason to see the marketing landing page.
  // Photographers/admins manage their business in the dashboard; customers
  // browse events.
  if (isAuthenticated) {
    const dest = user?.role === 'customer' ? ROUTES.EVENTS : ROUTES.DASHBOARD;
    return <Navigate to={dest} replace />;
  }

  const features = [
    {
      icon: <Camera className="w-8 h-8" />,
      title: 'Professional Quality',
      description: 'High-resolution photos captured by professional photographers',
    },
    {
      icon: <Download className="w-8 h-8" />,
      title: 'Instant Downloads',
      description: 'Download your photos immediately after purchase in multiple formats',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Easy Sharing',
      description: 'Share events with friends and family with secure access links',
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: 'Satisfaction Guaranteed',
      description: '100% satisfaction guarantee with professional customer support',
    },
  ];

  const stats = [
    { label: 'Events Hosted', value: '10,000+' },
    { label: 'Photos Sold', value: '500,000+' },
    { label: 'Happy Customers', value: '50,000+' },
    { label: 'Professional Photographers', value: '2,500+' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Professional Photo Sharing
              <span className="text-primary-200 block">Made Simple</span>
            </h1>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Discover, purchase, and download professional photos from events. Connect
              photographers with clients through our secure platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTES.EVENTS}>
                <Button
                  variant="secondary"
                  size="large"
                  icon={<ArrowRight size={20} />}
                  iconPosition="right"
                  className="w-full sm:w-auto"
                >
                  Browse Events
                </Button>
              </Link>
              <Link to={ROUTES.REGISTER}>
                <Button
                  variant="outline"
                  size="large"
                  className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-primary-600"
                >
                  Join as Photographer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Pixel Share?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide the best experience for both photographers and clients with our
              professional-grade platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Photos Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Photos</h2>
            <p className="text-xl text-gray-600">
              Stunning captures from our professional photographers
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="large" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {featuredPhotos.map((photo, index) => (
                <div
                  key={photo.id}
                  className="aspect-square group cursor-pointer overflow-hidden rounded-lg"
                >
                  <ResponsiveImage
                    src={photo.thumbnail}
                    alt={photo.title || `Featured photo ${index + 1}`}
                    className="w-full h-full group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-bold text-primary-200 mb-2">{stat.value}</div>
                <div className="text-primary-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join thousands of photographers and clients who trust Pixel Share for their event
            photography needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ROUTES.EVENTS}>
              <Button variant="primary" size="large" className="w-full sm:w-auto">
                Browse Events
              </Button>
            </Link>
            <Link to={ROUTES.REGISTER}>
              <Button
                variant="outline"
                size="large"
                className="w-full sm:w-auto border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                Sign Up Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
